import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import SideBar from "../common/sidebar";
import Footer from "../common/footer";
import "../../assets/css/desktop-app.css";

const MAC_DOWNLOAD_URL = "https://minutezilla.com/download";
const WIN_DOWNLOAD_URL = "https://minutezilla.com/download";

const POLICY_COPY = {
  disabled: {
    badge: "Not required",
    badgeClass: "da-policy-badge--off",
    get title() { return tr("Desktop tracking is not enabled for this workspace"); },
    get body() { return tr("Your workspace admin hasn't turned on desktop activity tracking. You can still install the app and use it for personal timer tracking."); },
  },
  optional: {
    badge: "Optional",
    badgeClass: "da-policy-badge--optional",
    get title() { return tr("Desktop tracking is optional for this workspace"); },
    get body() { return tr("Members can install the desktop companion at their discretion. There's no requirement from workspace admins."); },
  },
  required: {
    badge: "Required",
    badgeClass: "da-policy-badge--required",
    get title() { return tr("Desktop tracking is required by your workspace"); },
    get body() { return tr("Workspace admins want every member to install and run the desktop companion so activity metadata is reported."); },
  },
};

const PRIVACY_POINTS = [
  { icon: "✓", get label() { return tr("App name & process identifier"); } },
  { icon: "✓", get label() { return tr("Active window timestamps"); } },
  { icon: "✓", get label() { return tr("Idle state detection"); } },
  { icon: "✗", get label() { return tr("No screenshots or screen recording"); } },
  { icon: "✗", get label() { return tr("No keystrokes or clipboard content"); } },
  { icon: "✗", get label() { return tr("No emails, documents, or chat content"); } },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    get title() { return tr("Download & install"); },
    get body() { return tr("Get the installer for your operating system. macOS and Windows are supported."); },
  },
  {
    step: "02",
    get title() { return tr("Sign in once"); },
    get body() { return tr("Use your Minutezilla account to connect the desktop app to your workspace."); },
  },
  {
    step: "03",
    get title() { return tr("Track automatically"); },
    get body() { return tr("The app runs quietly in the background, capturing metadata as you work."); },
  },
];

export default function DesktopAppPage() {
  useTranslation();
  const auth = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState({
    tracking_enabled: false,
    enforcement: "OPTIONAL",
    privacy_mode: "METADATA_ONLY",
  });

  useEffect(() => {
    loadPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPolicy = async () => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json",
        },
      };
      const res = await axios.post(
        "/api/workspace/desktop-policy",
        JSON.stringify({ workspace_id: auth.workspace_id }),
        config
      );
      if (res.status === 200 && res.data.policy) {
        setPolicy(res.data.policy);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const policyState = !policy.tracking_enabled
    ? POLICY_COPY.disabled
    : policy.enforcement === "REQUIRED"
      ? POLICY_COPY.required
      : POLICY_COPY.optional;

  return (
    <>
      <SideBar />
      <div className="content-wrap dashboard-bg" id="wrapper">
        <div className="main">

          {/* ── Hero ───────────────────────────────────────────────── */}
          <div className="da-hero-wrap">
            <div className="da-hero-inner">
              <div className="da-hero-content">
                <div className="da-eyebrow">{tr("Desktop companion app")}</div>
                <h1 className="da-hero-title">{tr("Track time from your")}<br />{tr("desktop — automatically.")} </h1>
                <p className="da-hero-sub">{tr("A lightweight native app for macOS and Windows. Metadata-only, privacy-safe, and always in sync with your workspace.")} </p>

                {/* Policy status */}
                <div className="da-hero-policy">
                  {loading ? (
                    <div className="da-policy-loading">{tr("Loading workspace policy…")}</div>
                  ) : (
                    <>
                      <span className={`da-policy-badge ${policyState.badgeClass}`}>
                        {policyState.badge}
                      </span>
                      <span className="da-policy-text">{policyState.title}</span>
                    </>
                  )}
                </div>

                {/* Download buttons */}
                <div className="da-download-row">
                  <a
                    href={MAC_DOWNLOAD_URL}
                    className="da-download-btn da-download-btn--primary"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <svg className="da-os-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>{tr("Download for macOS")} </a>
                  <a
                    href={WIN_DOWNLOAD_URL}
                    className="da-download-btn da-download-btn--secondary"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <svg className="da-os-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 5.557L10.025 4.56l.004 6.835-7.019.04L3 5.558zm7.02 6.637l.006 6.859-7.022-1.003-.001-5.905 7.017.049zm.97-7.868L21 2.637v8.145l-10.01.078V4.326zm10.013 8.37l-.003 8.11-10.01-1.41-.014-6.733 10.027.033z"/>
                    </svg>{tr("Download for Windows")} </a>
                </div>

                <p className="da-gatekeeper-note">{tr("macOS users: right-click the app and choose Open on first launch if Gatekeeper blocks it.")} </p>
              </div>

              {/* App window illustration */}
              <div className="da-hero-visual" aria-hidden="true">
                <div className="da-mockup-window">
                  <div className="da-mockup-titlebar">
                    <span className="da-mockup-dot da-mockup-dot--red" />
                    <span className="da-mockup-dot da-mockup-dot--yellow" />
                    <span className="da-mockup-dot da-mockup-dot--green" />
                    <span className="da-mockup-appname">Minutezilla</span>
                  </div>
                  <div className="da-mockup-body">
                    {/* Sidebar strip */}
                    <div className="da-mockup-sidebar">
                      <div className="da-mockup-logo">
                        <span className="da-mockup-logo-dot" />
                      </div>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className={`da-mockup-navitem ${i === 1 ? "da-mockup-navitem--active" : ""}`} />
                      ))}
                      <div className="da-mockup-spacer" />
                      <div className="da-mockup-avatar" />
                    </div>
                    {/* Content */}
                    <div className="da-mockup-content">
                      <div className="da-mockup-timer-display">
                        <div className="da-mockup-timer-dot" />
                        <div className="da-mockup-timer-value">01:24:39</div>
                      </div>
                      <div className="da-mockup-field" />
                      <div className="da-mockup-field da-mockup-field--sm" />
                      <div className="da-mockup-tags">
                        <span className="da-mockup-tag" />
                        <span className="da-mockup-tag" />
                      </div>
                      <div className="da-mockup-btn" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Feature cards ──────────────────────────────────────── */}
          <div className="da-section">
            <div className="da-section-inner">
              <div className="da-cards-grid">

                {/* Privacy card */}
                <div className="da-card">
                  <div className="da-card-icon da-card-icon--privacy">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 2L3 5.5v5.25C3 14.52 6.13 17.97 10 19c3.87-1.03 7-4.48 7-8.25V5.5L10 2z"/>
                    </svg>
                  </div>
                  <h3>{tr("Privacy first")}</h3>
                  <p>{tr("Only metadata signals are collected — never your screen content.")}</p>
                  <ul className="da-privacy-list">
                    {PRIVACY_POINTS.map((pt) => (
                      <li key={pt.label} className={pt.icon === "✓" ? "da-privacy-yes" : "da-privacy-no"}>
                        <span className="da-privacy-icon">{pt.icon}</span>
                        {pt.label}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Workspace policy card */}
                <div className="da-card">
                  <div className="da-card-icon da-card-icon--policy">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="2" width="14" height="16" rx="2"/>
                      <line x1="7" y1="7" x2="13" y2="7"/>
                      <line x1="7" y1="10" x2="13" y2="10"/>
                      <line x1="7" y1="13" x2="10" y2="13"/>
                    </svg>
                  </div>
                  <h3>{tr("Workspace policy")}</h3>
                  <p>{tr("Your workspace's current desktop tracking configuration.")}</p>
                  {loading ? (
                    <div className="da-policy-skeleton">
                      <div className="da-policy-sk-row" />
                      <div className="da-policy-sk-row" />
                      <div className="da-policy-sk-row" />
                    </div>
                  ) : (
                    <div className="da-policy-table">
                      <div className="da-policy-row">
                        <span className="da-policy-label">{tr("Tracking")}</span>
                        <span className={`da-policy-val ${policy.tracking_enabled ? "da-policy-val--on" : "da-policy-val--off"}`}>
                          {policy.tracking_enabled ? tr("Enabled") : tr("Off")}
                        </span>
                      </div>
                      <div className="da-policy-row">
                        <span className="da-policy-label">{tr("Requirement")}</span>
                        <span className="da-policy-val">
                          {policy.tracking_enabled ? policy.enforcement : "N/A"}
                        </span>
                      </div>
                      <div className="da-policy-row">
                        <span className="da-policy-label">{tr("Privacy mode")}</span>
                        <span className="da-policy-val">{policy.privacy_mode}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Setup card */}
                <div className="da-card">
                  <div className="da-card-icon da-card-icon--setup">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="14" height="11" rx="2"/>
                      <line x1="7" y1="17" x2="13" y2="17"/>
                      <line x1="10" y1="15" x2="10" y2="17"/>
                    </svg>
                  </div>
                  <h3>macOS &amp; Windows</h3>
                  <p>{tr("Native builds for both platforms. Runs quietly in the menu bar or system tray.")}</p>
                  <div className="da-platform-list">
                    <div className="da-platform-item">
                      <svg viewBox="0 0 24 24" fill="currentColor" className="da-platform-icon">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      <div>
                        <div className="da-platform-name">macOS</div>
                        <div className="da-platform-sub">{tr("Universal binary · macOS 11+")}</div>
                      </div>
                    </div>
                    <div className="da-platform-item">
                      <svg viewBox="0 0 24 24" fill="currentColor" className="da-platform-icon">
                        <path d="M3 5.557L10.025 4.56l.004 6.835-7.019.04L3 5.558zm7.02 6.637l.006 6.859-7.022-1.003-.001-5.905 7.017.049zm.97-7.868L21 2.637v8.145l-10.01.078V4.326zm10.013 8.37l-.003 8.11-10.01-1.41-.014-6.733 10.027.033z"/>
                      </svg>
                      <div>
                        <div className="da-platform-name">Windows</div>
                        <div className="da-platform-sub">{tr("Installer · Windows 10+")}</div>
                      </div>
                    </div>
                  </div>
                  <div className="da-card-actions">
                    <a
                      href={MAC_DOWNLOAD_URL}
                      className="da-btn-primary"
                      target="_blank"
                      rel="noreferrer"
                    >{tr("Download now")} </a>
                    <Link to="/settings" className="da-btn-ghost">{tr("Workspace settings")}</Link>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* ── How it works ───────────────────────────────────────── */}
          <div className="da-how-section">
            <div className="da-section-inner">
              <div className="da-how-header">
                <div className="da-eyebrow-light">{tr("How it works")}</div>
                <h2 className="da-how-title">{tr("Up and running in three steps")}</h2>
              </div>
              <div className="da-how-grid">
                {HOW_IT_WORKS.map((item) => (
                  <div className="da-how-item" key={item.step}>
                    <div className="da-how-step">{item.step}</div>
                    <h4 className="da-how-name">{item.title}</h4>
                    <p className="da-how-body">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
